<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {NotificationTemplate as INotificationTemplate} from "@/notification-template/interfaces";
import {customerCertificationApi, notificationSequenceApi, notificationTemplateApi} from "@/container";
import {usePatientStore} from "@/stores/patient.store";
import NotificationTemplate from "@/notification-template/components/NotificationTemplate.vue";
import {storeToRefs} from "pinia";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Send} from "@lucide/vue";
import {Dialog, DialogTrigger} from "@/core/components/ui/dialog";
import PreCertificationDialogContent
    from "@/customer-certification/components/pre-certification-dialog/PreCertificationDialogContent.vue";
import formatHelper from "@/core/helpers/formatHelper";
import {useCreditStore} from "@/stores/credit.store";
import {pluralize} from "@/core/filters/Pluralize";
import {useToastStore} from "@/stores/toast.store";
import {NotificationSequenceDetails} from "@/notification-sequences/interfaces/NotificationSequence";
import NotificationSequenceActivation
    from "@/notification-sequences/components/notification-sequence-activation/NotificationSequenceActivation.vue";

const props = defineProps<{
    surveyId: number;
    consumerKey: string;
}>();

const emits = defineEmits<{
    (e: 'sent'): void
}>();

const patientStore = usePatientStore();
const creditStore = useCreditStore();
const {showSuccess, showError} = useToastStore();

const {credit} = storeToRefs(creditStore);
const {customer} = storeToRefs(patientStore);

const templates = ref<INotificationTemplate[]>();
const sequences = ref<NotificationSequenceDetails[]>();

const isSending = ref({});

onMounted(() => {
    if (customer.value?.consumer_key !== props.consumerKey) {
        return;
    }

    creditStore.refresh();

    getSurveyTemplates();
    getNotificationSequences();
});

/**
 * Fetch notification templates for the survey.
 */
const getSurveyTemplates = async () => {
    return notificationTemplateApi().index({
        customer_id: customer.value?.id,
        survey_id: props.surveyId,
        type: 'rcs'
    }).then((data) => {
        templates.value = data;
    });
};

const getNotificationSequences = async () => {
    return notificationSequenceApi().index({
        survey_id: props.surveyId,
        with: 'templates'
    }).then((data) => {
        sequences.value = data;
    });
};

const uncertified = computed(() => {
    return customer.value?.status === 'none';
});

const handleCertified = async () => {
    return await patientStore.refreshCustomer();
};

const hasEnoughCredit = (template: INotificationTemplate) => {
    if (!credit.value) {
        return false;
    }

    return credit.value.balance >= template.credits;
};

const certification = computed(() => {
    if (!customer.value) {
        return null;
    }

    return customer.value.certification || customer.value.pending_certification;
});

const send = async (template: INotificationTemplate): Promise<void> => {
    if (!certification.value) {
        return;
    }

    isSending.value[template.id] = true;

    try {
        await customerCertificationApi().createNotification(certification.value.id, {
            notification_template_id: template.id
        });

        showSuccess({
            content: 'Le message a été envoyé avec succès'
        });

        creditStore.refresh();
        emits('sent');
    } catch {
        showError({
            content: 'Une erreur est survenue lors de l\'envoi du message'
        });
    } finally {
        isSending.value[template.id] = false;
    }
};
</script>

<template>
  <div v-if="templates?.length || sequences?.length" class="flex flex-col gap-4 p-4">
    <h2>Accompagnez votre patient en lui envoyant les conseils adaptés</h2>
    <div
      v-if="templates"
      :class="templates.length >= 2 ? 'flex flex-row flex-wrap gap-8 justify-center' : 'flex flex-col gap-8'"
    >
      <div
        v-for="template in templates"
        :key="template.id"
        :class="templates.length >= 2 ? 'flex flex-col items-center gap-3' : 'flex-col flex lg:flex-row items-center justify-center flex-wrap'"
      >
        <NotificationTemplate :template="template" class="max-w-sm" />
        <div v-if="!hasEnoughCredit(template)" class="text-end">
          <router-link to="/patient-administration/communication/message/store">
            <BaseButton density="comfortable">
              Acheter des crédits
            </BaseButton>
          </router-link>
          <p class="text-xs mt-1">
            {{ credit?.balance ?? 0 }} {{ pluralize('crédit', credit?.balance ?? 0) }} restants
          </p>
        </div>
        <Dialog v-else-if="uncertified && customer">
          <DialogTrigger>
            <BaseButton primary density="comfortable">
              Envoyer le RCS
              <Send class="ml-2 w-4" />
            </BaseButton>
          </DialogTrigger>
          <PreCertificationDialogContent :customer="customer" :on-certified="() => handleCertified()" />
        </Dialog>

        <div v-else-if="customer?.information?.phone">
          <BaseButton
            :disabled="!hasEnoughCredit(template) || Object.values(isSending).some(sending => sending === true)"
            primary
            class="self-center"
            density="comfortable"
            :loading="isSending[template.id]"
            @click="send(template)"
          >
            Envoyer au {{ formatHelper.readablePhone(customer.information.phone) }}
            <Send class="ml-2 w-4" />
          </BaseButton>
        </div>
      </div>
    </div>
    <div v-if="sequences?.length && customer" class="bg-muted p-4 rounded-md flex flex-wrap gap-24 items-center">
      <NotificationSequenceActivation
        v-for="sequence in sequences"
        :key="sequence.id"
        :sequence="sequence"
        :customer="customer"
        :consumer-key="consumerKey"
        @started="emits('sent')"
        @certified="handleCertified()"
      />
    </div>
  </div>
</template>

<style scoped>

</style>
